((root, factory) => {
  const api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }
  const initialise = () => api.mount(root, document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialise, { once: true });
  else initialise();
  document.addEventListener('shopify:section:load', initialise);
  document.addEventListener('shopify:section:unload', (event) => api.unmountCartControls(event.target));
})(typeof window !== 'undefined' ? window : globalThis, () => {
  'use strict';
  const draftKey = 'dim:dream-it:draft:v4';
  const sessionKey = `${draftKey}:image-session`;
  const databaseName = 'dim-dream-it';
  const storeName = 'generated-images';
  const cartControlMounts = new WeakMap();

  const draftImageKeys = (raw) => {
    try {
      const draft = JSON.parse(raw || 'null');
      return new Set([draft?.imageKey, draft?.originalImageKey].filter((key) => typeof key === 'string' && key));
    } catch (_) { return new Set(); }
  };

  const openExistingDatabase = (win) => new Promise((resolve, reject) => {
    if (!win.indexedDB) { resolve(null); return; }
    const request = win.indexedDB.open(databaseName, 1);
    let absent = false;
    request.onupgradeneeded = () => {
      absent = true;
      try { request.transaction.abort(); } catch (_) {}
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      if (absent || request.error?.name === 'AbortError') resolve(null);
      else reject(request.error || new Error('Draft storage is unavailable.'));
    };
    request.onblocked = () => reject(new Error('Draft storage is busy.'));
  });

  const deleteCurrentDraftImages = async (win, currentSession, keys) => {
    const db = await openExistingDatabase(win);
    if (!db) return 0;
    try {
      if (!db.objectStoreNames.contains(storeName)) return 0;
      return await new Promise((resolve, reject) => {
        const transaction = db.transaction(storeName, 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.openCursor();
        let removed = 0;
        request.onsuccess = () => {
          const cursor = request.result;
          if (!cursor) return;
          const record = cursor.value;
          const belongsToTab = Boolean(currentSession && record?.sessionId === currentSession) || keys.has(String(record?.id || cursor.primaryKey));
          if (belongsToTab) { cursor.delete(); removed += 1; }
          cursor.continue();
        };
        request.onerror = () => reject(request.error || new Error('Draft images could not be read.'));
        transaction.oncomplete = () => resolve(removed);
        transaction.onerror = () => reject(transaction.error || new Error('Draft images could not be cleared.'));
        transaction.onabort = () => reject(transaction.error || new Error('Draft image cleanup was interrupted.'));
      });
    } finally { db.close(); }
  };

  const clearCurrentTabDraft = async (win) => {
    if (win.DIMRuntime?.enabled) {
      const scene = win.DIMRuntime.scene;
      if (!scene) throw new Error('The current design could not be cleared.');
      const saved = scene.readHeader();
      if (!['ready', 'empty', 'expired'].includes(saved.status)) throw new Error('Open this design in its compatible editor before clearing it.');
      const payload = saved.payload || saved.record?.payload;
      const keys = new Set([payload?.imageKey, payload?.originalImageKey, payload?.retainedUploadKey].filter(Boolean));
      for (const key of keys) await scene.deleteImageRecord(key);
      await scene.remove();
      win.sessionStorage.removeItem(`${scene.keys.draft}:image-session`);
      return;
    }
    const draftText = win.sessionStorage.getItem(draftKey) || '';
    const currentSession = win.sessionStorage.getItem(sessionKey) || '';
    await deleteCurrentDraftImages(win, currentSession, draftImageKeys(draftText));
    win.sessionStorage.removeItem(draftKey);
    win.sessionStorage.removeItem(sessionKey);
  };

  const afterNextPaint = (win) => new Promise((resolve) => {
    if (typeof win.requestAnimationFrame === 'function') {
      win.requestAnimationFrame(() => win.requestAnimationFrame(() => resolve()));
      return;
    }
    if (typeof win.setTimeout === 'function') {
      win.setTimeout(resolve, 0);
      return;
    }
    resolve();
  });

  const mountCartControls = (section, win) => {
    if (!section || section.dataset.cartControlsInitialised === 'true') return;
    const form = section.querySelector('#cart');
    const update = form?.querySelector('[data-cart-update]');
    const quantityInputs = form ? Array.from(form.querySelectorAll('[data-cart-quantity-input]')) : [];
    if (!form || !update || !quantityInputs.length) return;
    section.dataset.cartControlsInitialised = 'true';
    const notice = form.querySelector('[data-cart-changes]');
    const summaryNotices = Array.from(section.ownerDocument?.querySelectorAll?.('[data-cart-summary-changes]') || []);
    const checkoutControls = Array.from(section.ownerDocument?.querySelectorAll?.('[name="checkout"][form="cart"]') || []);
    const checkoutDisabled = new Map(checkoutControls.map((control) => [control, Boolean(control.disabled)]));
    let pending = false;
    const dirty = () => quantityInputs.some((input) => input.value !== input.defaultValue);
    const syncUpdate = () => {
      const changed = dirty();
      form.dataset.cartDirty = String(changed);
      form.dataset.cartPending = String(pending);
      update.hidden = !changed && !pending;
      // Keep the native submitter enabled during its initial POST so name=update is included.
      update.disabled = !changed && !pending;
      update.setAttribute?.('aria-busy', String(pending));
      [notice, ...summaryNotices].filter(Boolean).forEach((message) => {
        message.hidden = !changed && !pending;
        message.textContent = pending ? 'Updating your basket. Please wait before continuing to checkout.'
          : 'Quantity changes are not saved. Prices and totals still show your saved basket. Select Update basket before checkout.';
      });
      checkoutControls.forEach((control) => {
        if (changed || pending) {
          if (!checkoutDisabled.get(control)) control.dataset.cartQuantityLock = 'true';
          control.disabled = true;
        } else if (control.dataset.cartQuantityLock === 'true') {
          control.disabled = checkoutDisabled.get(control);
          delete control.dataset.cartQuantityLock;
        }
      });
    };
    const onSubmit = (event) => {
      const submitter = event.submitter;
      if (pending || (dirty() && submitter?.name === 'checkout')) {
        event.preventDefault(); syncUpdate();
        notice?.focus?.();
        return;
      }
      if (submitter === update || submitter?.name === 'update') { pending = true; syncUpdate(); }
    };
    const onReset = () => {
      Promise.resolve().then(syncUpdate);
    };
    const onPageShow = () => { pending = false; syncUpdate(); };
    quantityInputs.forEach((input) => {
      input.addEventListener('input', syncUpdate);
      input.addEventListener('change', syncUpdate);
    });
    form.addEventListener('reset', onReset);
    form.addEventListener('submit', onSubmit);
    win?.addEventListener?.('pageshow', onPageShow);
    cartControlMounts.set(section, { form, quantityInputs, syncUpdate, onReset, onSubmit, onPageShow, win });
    syncUpdate();
  };

  const unmountOneCartControls = (section) => {
    const mounted = cartControlMounts.get(section);
    if (!mounted) return;
    mounted.quantityInputs.forEach((input) => {
      input.removeEventListener?.('input', mounted.syncUpdate);
      input.removeEventListener?.('change', mounted.syncUpdate);
    });
    mounted.form.removeEventListener?.('reset', mounted.onReset);
    mounted.form.removeEventListener?.('submit', mounted.onSubmit);
    mounted.win?.removeEventListener?.('pageshow', mounted.onPageShow);
    cartControlMounts.delete(section);
    delete section.dataset.cartControlsInitialised;
  };

  const unmountCartControls = (scope) => {
    if (!scope) return;
    const sections = new Set();
    if (cartControlMounts.has(scope) || scope.matches?.('.ux-basket')) sections.add(scope);
    scope.querySelectorAll?.('.ux-basket').forEach((section) => sections.add(section));
    sections.forEach(unmountOneCartControls);
  };

  const mount = (win, doc) => {
    doc.querySelectorAll('.ux-basket').forEach((section) => mountCartControls(section, win));
    doc.querySelectorAll('[data-basket-add-more-dialog]').forEach((dialog) => {
      if (dialog.dataset.initialised === 'true') return;
      dialog.dataset.initialised = 'true';
      const section = dialog.closest('.ux-basket');
      const open = section?.querySelector('[data-basket-add-more-open]');
      const confirm = dialog.querySelector('[data-basket-add-more-confirm]');
      const cancel = dialog.querySelector('[data-basket-add-more-cancel]');
      const status = dialog.querySelector('[data-basket-add-more-status]');
      const defaultConfirmLabel = confirm?.textContent || '';
      const busyLabel = section?.dataset.chooseBusy || 'Opening Choose it…';
      let busy = false;
      open?.addEventListener('click', () => {
        if (typeof dialog.showModal === 'function') dialog.showModal();
      });
      dialog.addEventListener('close', () => {
        if (!busy && open?.isConnected) open.focus({ preventScroll: true });
      });
      dialog.addEventListener('cancel', (event) => {
        if (busy) event.preventDefault();
      });
      confirm?.addEventListener('click', async () => {
        if (busy) return;
        busy = true;
        confirm.disabled = true;
        confirm.setAttribute('aria-busy', 'true');
        confirm.textContent = busyLabel;
        if (cancel) cancel.disabled = true;
        dialog.setAttribute('aria-busy', 'true');
        if (status) {
          status.textContent = busyLabel;
          status.hidden = false;
        }
        try {
          await afterNextPaint(win);
          await clearCurrentTabDraft(win);
          const destination = section?.dataset.chooseUrl || '/collections/all';
          win.location.assign(destination);
        } catch (_) {
          busy = false;
          confirm.disabled = false;
          confirm.removeAttribute('aria-busy');
          confirm.textContent = defaultConfirmLabel;
          if (cancel) cancel.disabled = false;
          dialog.removeAttribute('aria-busy');
          if (status) {
            status.textContent = section?.dataset.clearError || 'We could not clear this draft. Your basket is unchanged; please try again.';
            status.hidden = false;
            status.setAttribute('tabindex', '-1');
            status.focus({ preventScroll: true });
          }
        }
      });
    });
  };

  return Object.freeze({ draftImageKeys, deleteCurrentDraftImages, clearCurrentTabDraft, afterNextPaint, mountCartControls, unmountCartControls, mount });
});
